import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import SolidPlaneDepartureIcon from 'LuxKit/Icons/solid/SolidPlaneDepartureIcon'
import TextLink from 'LuxKit/TextLink'
import Colour from 'components/utils/Colour'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useLuxPlusFlightVoucherBanner from 'luxPlus/hooks/useLuxPlusFlightVoucherBanner'
import { LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN } from 'luxPlus/constants/base'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import type { PageBannerCarouselItem } from 'LuxKit/Banners/types'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'

function useLuxPlusFlightVoucherOfferBannerItem(): PageBannerCarouselItem | null {
  const {
    showBanner,
    onDismiss,
    ctaLabel,
    ctaTo,
    ctaHref,
    ctaOnClick,
  } = useLuxPlusFlightVoucherBanner()
  const formatCurrency = useCurrencyFormatter()
  const maxSavings = formatCurrency(800, 'roundedDollar')

  return useMemo(() => {
    if (!showBanner) return null

    return {
      id: 'luxplus-flight-voucher-offer',
      kind: 'lux-plus',
      icon: <SolidPlaneDepartureIcon size="M" />,
      onDismiss,
      actionButtonProps: {
        kind: 'secondary',
        children: ctaLabel,
        to: ctaTo,
        href: ctaHref,
        onClick: ctaOnClick,
      },
      hideMobileActionButton: true,
      children: <>
        <CSSBreakpoint min="desktop">
          <FormattedMessage
            id="JcU4oIFlI6qmhrvQQUm_"
            defaultMessage="<luxPlusLabel>LuxPlus<plus>+</plus></luxPlusLabel> Exclusive! SAVE up to {maxSavings}pp on international return flights. Ends {endDate}. T&Cs apply."
            description="Desktop LuxPlus flight voucher promotion showing per-person savings amount and campaign end date"
            values={{
              luxPlusLabel: chunks => <b>{chunks}</b>,
              plus: chunks => <Colour value="highlight-lux-plus">{chunks}</Colour>,
              maxSavings,
              endDate: <FormatDateTime value={LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.END_DATE} day="numeric" month="short" year="numeric" />,
            }}
          />
        </CSSBreakpoint>
        <CSSBreakpoint max="tablet">
          <FormattedMessage
            id="I80VNjAAHJ_QB2Xretup"
            defaultMessage="<luxPlusLabel>LuxPlus<plus>+</plus></luxPlusLabel> Exclusive! SAVE up to {maxSavings}pp on international return flights. Ends {endDate}. T&Cs apply. <ctaLink>{ctaLabel}</ctaLink>"
            description="Mobile LuxPlus flight voucher promotion showing per-person savings amount, campaign end date, and inline call-to-action link"
            values={{
              luxPlusLabel: chunks => <b>{chunks}</b>,
              plus: chunks => <Colour value="highlight-lux-plus">{chunks}</Colour>,
              maxSavings,
              endDate: <FormatDateTime value={LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.END_DATE} day="numeric" month="short" year="numeric" />,
              ctaLabel,
              ctaLink: chunks => (
                <TextLink
                  key="flight-voucher-mobile-cta-link"
                  to={ctaTo}
                  href={ctaHref}
                  onClick={ctaOnClick}
                  size="caption"
                >
                  {chunks}
                </TextLink>
              ),
            }}
          />
        </CSSBreakpoint>
      </>,
    }
  }, [showBanner, onDismiss, ctaLabel, ctaTo, ctaHref, ctaOnClick, maxSavings])
}

export default useLuxPlusFlightVoucherOfferBannerItem
